import { Button, Flex, IconButton, Select, Text } from "@chakra-ui/react";
import {
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

// Mesmo padrão de paginação da tela de Leilão
// (src/components/CreditAuctionProposalsPage/credit-auction-proposals-pagination),
// adaptado para paginar a listagem de empresas deste redesign.

type Props = {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  // Nome do item paginado no singular, usado no rótulo "Total de X ...".
  // Default "empresa" (usado por Vínculos Ativos, que pagina por empresa);
  // Vínculos Encerrados passa "vínculo" e Novos Vínculos passa "tomador"
  // (paginam por CCB).
  itemLabel?: string;
  // Plural do item, quando não é só "singular + s" (ex.: "tomador" →
  // "tomadores"). Default: itemLabel + "s".
  itemLabelPlural?: string;
  // Opções do seletor "por página". Default: 5, 10 e 20.
  pageSizeOptions?: number[];
};

const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 20];

const SIBLINGS = 1;
const BOUNDARIES = 1;

function getVisiblePages(
  currentPage: number,
  totalPages: number
): (number | "ellipsis-left" | "ellipsis-right")[] {
  if (totalPages <= 1) return [1];

  const startPages = Array.from(
    { length: Math.min(BOUNDARIES, totalPages) },
    (_, i) => i + 1
  );
  const endPages = Array.from(
    { length: Math.min(BOUNDARIES, totalPages) },
    (_, i) => totalPages - i
  ).reverse();

  const siblingsStart = Math.max(currentPage - SIBLINGS, BOUNDARIES + 1);
  const siblingsEnd = Math.min(currentPage + SIBLINGS, totalPages - BOUNDARIES);

  const middle: number[] = [];
  for (let p = siblingsStart; p <= siblingsEnd; p++) middle.push(p);

  const result: (number | "ellipsis-left" | "ellipsis-right")[] = [...startPages];
  if (siblingsStart > BOUNDARIES + 1) result.push("ellipsis-left");
  result.push(...middle);
  if (siblingsEnd < totalPages - BOUNDARIES) result.push("ellipsis-right");
  for (const page of endPages) {
    if (!result.includes(page)) result.push(page);
  }

  return result;
}

export default function VinculosPagination({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  itemLabel = "empresa",
  itemLabelPlural,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}: Props) {
  if (!totalItems) return null;

  const isFirst = currentPage <= 1;
  const isLast = currentPage >= totalPages;
  const visiblePages = getVisiblePages(currentPage, totalPages);

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      mt={4}
      mb={4}
      gap={2}
      flexWrap="wrap"
      fontFamily="Lexend Deca"
    >
      <IconButton
        icon={<FaAngleDoubleLeft />}
        onClick={() => onPageChange(1)}
        isDisabled={isFirst}
        aria-label="Primeira página"
        size="sm"
        variant="ghost"
        colorScheme="primary"
      />
      <IconButton
        icon={<FaChevronLeft />}
        onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
        isDisabled={isFirst}
        aria-label="Página anterior"
        size="sm"
        variant="ghost"
        colorScheme="primary"
      />

      <Flex gap={1}>
        {visiblePages.map((page) =>
          page === "ellipsis-left" || page === "ellipsis-right" ? (
            <Text
              key={page}
              minW="32px"
              h="32px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="gray.500"
              fontSize="sm"
            >
              …
            </Text>
          ) : (
            <Button
              key={page}
              size="sm"
              minW="32px"
              h="32px"
              px={2}
              variant={page === currentPage ? "solid" : "ghost"}
              colorScheme="primary"
              onClick={() => onPageChange(page)}
              fontFamily="Lexend Deca"
              fontWeight={page === currentPage ? "600" : "normal"}
            >
              {page}
            </Button>
          )
        )}
      </Flex>

      <IconButton
        icon={<FaChevronRight />}
        onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
        isDisabled={isLast}
        aria-label="Próxima página"
        size="sm"
        variant="ghost"
        colorScheme="primary"
      />
      <IconButton
        icon={<FaAngleDoubleRight />}
        onClick={() => onPageChange(totalPages)}
        isDisabled={isLast}
        aria-label="Última página"
        size="sm"
        variant="ghost"
        colorScheme="primary"
      />

      <Flex alignItems="center" gap={2} ml={3}>
        <Text fontSize="sm" color="gray.600">
          por página
        </Text>
        <Select
          value={pageSize}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
          size="sm"
          width="auto"
          borderRadius="md"
          fontFamily="Lexend Deca"
        >
          {pageSizeOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      </Flex>

      <Text fontSize="sm" color="gray.600" ml={3}>
        Total de {totalItems.toLocaleString("pt-BR")}{" "}
        {totalItems === 1 ? itemLabel : itemLabelPlural ?? `${itemLabel}s`}
      </Text>
    </Flex>
  );
}
